'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { mergeAriaIds } from '../../../lib/aria';
import { developmentDiagnosticsEnabled } from '../../../lib/diagnostics';
import { cn } from '../../../lib/utils';
import { SpecraInput } from '../ensigns/input';
import { SpecraSelect } from '../ensigns/select';
import { SpecraTextarea } from '../ensigns/textarea';
import { SpecraText } from '../ensigns/typography';
/** Label, control, hint, and error contract shared by all product forms. */ export const SpecraFormField = /*#__PURE__*/ React.forwardRef(({ children, className, error, hint, htmlFor, label, labelAdornment, required = false, ...props }, ref)=>{
    const generatedId = React.useId();
    const childId = /*#__PURE__*/ React.isValidElement(children) && typeof children.props.id === 'string' ? children.props.id : undefined;
    const controlId = htmlFor ?? childId ?? `specra-field-${generatedId}`;
    const labelId = `${controlId}-label`;
    const hintId = `${controlId}-hint`;
    const errorId = `${controlId}-error`;
    const describedBy = mergeAriaIds(hint ? hintId : undefined, error ? errorId : undefined);
    const isNativeControl = /*#__PURE__*/ React.isValidElement(children) && typeof children.type === 'string' && [
        'input',
        'select',
        'textarea'
    ].includes(children.type);
    const canReceiveAriaProps = /*#__PURE__*/ React.isValidElement(children) && children.type !== React.Fragment && (typeof children.type !== 'string' || isNativeControl);
    const canReceiveRequired = canReceiveAriaProps && (isNativeControl || children.type === SpecraInput || children.type === SpecraSelect || children.type === SpecraTextarea);
    React.useEffect(()=>{
        if (developmentDiagnosticsEnabled && required && !canReceiveRequired && !htmlFor) {
            console.warn('SpecraFormField cannot forward required to this composite child. Apply required and aria-required to the underlying control.');
        }
    }, [
        canReceiveRequired,
        htmlFor,
        required
    ]);
    React.useEffect(()=>{
        if (developmentDiagnosticsEnabled && htmlFor && childId && htmlFor !== childId) {
            console.warn(`SpecraFormField htmlFor "${htmlFor}" overrides child id "${childId}" so the label and control remain associated.`);
        }
    }, [
        childId,
        htmlFor
    ]);
    const labelTargetsControl = canReceiveAriaProps || Boolean(htmlFor);
    const decoratedControl = canReceiveAriaProps ? /*#__PURE__*/ React.cloneElement(children, {
        id: controlId,
        'aria-describedby': mergeAriaIds(children.props['aria-describedby'], describedBy),
        'aria-invalid': children.props['aria-invalid'] ?? (error ? true : undefined),
        ...canReceiveRequired ? {
            required: children.props.required ?? required
        } : {}
    }) : children;
    const control = canReceiveAriaProps ? decoratedControl : /*#__PURE__*/ _jsx("fieldset", {
        "aria-describedby": describedBy,
        "aria-labelledby": labelTargetsControl ? undefined : labelId,
        className: "m-0 min-w-0 border-0 p-0",
        children: decoratedControl
    });
    const labelElement = /*#__PURE__*/ _jsxs("label", {
        className: "block text-[length:var(--fs-ui-caption)] font-medium text-foreground",
        htmlFor: labelTargetsControl ? controlId : undefined,
        id: labelId,
        children: [
            label,
            required ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        "aria-hidden": "true",
                        className: "ml-0.5 text-danger",
                        children: "*"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "sr-only",
                        children: " (required)"
                    })
                ]
            }) : null
        ]
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('space-y-1', className),
        ref: ref,
        ...props,
        children: [
            labelAdornment ? /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1",
                children: [
                    labelElement,
                    labelAdornment
                ]
            }) : labelElement,
            control,
            hint ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                id: hintId,
                size: "caption",
                tone: "muted",
                children: hint
            }) : null,
            error ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                id: errorId,
                role: "alert",
                size: "caption",
                tone: "danger",
                children: error
            }) : null
        ]
    });
});
SpecraFormField.displayName = 'SpecraFormField';
